import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { linkTelegram, unlinkTelegram, getTelegramStatus } from '../services/notificationsService';
import { confirmDialog } from '../components/Common/confirm';

/* Подключение Telegram-бота из профиля — общее для ученика и админа.

   Включили тумблер → сервер выдал ссылку на бота с одноразовым кодом → открываем
   её в новой вкладке → пока человек жмёт в боте «Начать», страница раз в 2 секунды
   спрашивает сервер, привязался ли аккаунт. Как только да — тумблер загорается.
   Ждём 3 минуты, дальше перестаём: значит, передумали. */
const POLL_MS = 2000;
const GIVE_UP_MS = 3 * 60 * 1000;

export default function useTelegramLink(initial) {
  const [tg, setTg] = useState(initial || { available: false, connected: false, username: null });
  const [applied, setApplied] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);   // ссылка открыта, ждём «Начать»
  const timer = useRef(null);

  /* Состояние приходит вместе с настройками уведомлений — и подхватываем мы его
     прямо во время отрисовки, а не в useEffect: иначе тумблер успевает
     мигнуть выключенным и на глазах «переехать» в нужное положение.
     `ready` говорит странице, что можно показывать тумблер вместо заглушки. */
  if (initial && initial !== applied) { setApplied(initial); setTg(initial); }
  const ready = !!applied;

  const stop = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    setWaiting(false);
  }, []);

  useEffect(() => stop, [stop]);

  const toggle = useCallback(async (on) => {
    if (busy) return;
    if (!on) {
      // отвязка молча теряет канал уведомлений — спрашиваем
      const ok = await confirmDialog({
        title: 'Отвязать Telegram?',
        message: 'Уведомления перестанут приходить в бота — останутся только на платформе. Подключить обратно можно в любой момент этим же тумблером.',
        confirmText: 'Отвязать',
      });
      if (!ok) return;
      stop();
      setBusy(true);
      try {
        const r = await unlinkTelegram();
        setTg(r.data);
        toast.success('Telegram отключён');
      } catch {
        toast.error('Не удалось отключить');
      } finally { setBusy(false); }
      return;
    }

    // вкладку открываем сразу по клику, иначе браузер посчитает её всплывающей
    const tab = window.open('', '_blank');
    setBusy(true);
    try {
      const r = await linkTelegram();
      const url = r.data?.url;
      if (!url) throw new Error('нет ссылки');
      if (tab) tab.location.href = url; else window.location.href = url;

      stop();                       // на случай, если тумблер дёрнули повторно
      setWaiting(true);
      const until = Date.now() + GIVE_UP_MS;
      timer.current = setInterval(async () => {
        if (Date.now() > until) return stop();
        try {
          const s = await getTelegramStatus();
          if (s.data?.connected) {
            setTg(s.data);
            stop();
            toast.success('Telegram подключён');
          }
        } catch { /* сеть моргнула — попробуем на следующем круге */ }
      }, POLL_MS);
    } catch (e) {
      if (tab) tab.close();
      toast.error(e?.response?.data?.message || 'Бот пока не подключён');
    } finally { setBusy(false); }
  }, [busy, stop]);

  return { tg, ready, busy, waiting, toggle };
}
